// Composer Studio — 양식 탐색기(좌측 페이지 레벨 레일, 결정 2026-09-20-composer-template-explorer).
//
// 헤더 콤보박스를 대체하는 **양식 집합**의 표면: 검색·공개 필터·분류 트리·드래그 배정·다중 선택
// 일괄 이동·분류 헤더/행 ⋯ 메뉴. 「열린 양식 하나」를 다루는 저장·복제·삭제·공개는 헤더에 남고,
// 행 ⋯ 메뉴는 같은 핸들러를 호스트 콜백으로 부를 뿐 정본은 하나다(§3 이동표).
//
// 책임 경계:
//   - 분류 **배정**(PATCH templates/:key { categoryId })과 분류 이름/순서/삭제는 여기서 직접 API를
//     부른다 — 에디터의 dirty·undo와 무관한 양식 집합 조작이라 호스트 상태를 거칠 이유가 없다.
//     반영 뒤 `onAssigned`/`onCategoriesChanged`로 호스트가 목록·분류를 다시 읽는다.
//   - **열기**는 반드시 호스트의 `onOpen`(= EditorPage.handleLoad — 미저장 확인 모달 경로)이다.
//     여기서 performLoad를 우회하면 저장하지 않은 변경이 조용히 사라진다(§5).
//   - 새 양식·가져오기·복제·공개·삭제·분류 관리 모달은 호스트의 기존 핸들러를 그대로 부른다.
// 순서·필터 규칙은 `templateCategoryOptions.ts`의 순수 함수(spec 대상)가 정본이다.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmModal } from '@safetyhub/ui';
import {
  adminApi,
  extractApiError,
  type ReportTemplateCategory,
  type StoredReportTemplate,
} from '../../lib/api';
import { withDirectionParticle, withObjectParticle } from '@safetyhub/shared';
import { useToastStore } from '../../stores/toast';
import { ComposerContextMenu, type ComposerContextMenuItem } from './ComposerContextMenu';
import {
  buildExplorerTree,
  UNCATEGORIZED_LABEL,
  type ExplorerTreeGroup,
  type ExplorerVisibility,
} from './templateCategoryOptions';

/** 탐색기 레일 폭(px) — 접힘 아이콘 레일 / 펼침. 셸 패널 파라미터와 EditorPage 배선이 공유. */
export const EXPLORER_COMPACT_PX = 48;
export const EXPLORER_EXPANDED_PX = 260;

/**
 * 분류 위/아래 이동 계획(순수) — 관리 모달(`planCategorySave`)과 같은 규약: **배열 인덱스가 곧 최종
 * sortOrder**라 서버가 알던 값과 다른 행만 PATCH한다. 이웃 둘만 바꾸면 동점 sortOrder(이름 순 보조
 * 정렬)에서 순서가 어긋나므로 전체를 인덱스로 정규화한다.
 */
export function planCategoryMove(
  categories: readonly ReportTemplateCategory[],
  id: string,
  dir: -1 | 1,
): { id: string; sortOrder: number }[] {
  const idx = categories.findIndex((c) => c.id === id);
  const target = idx + dir;
  if (idx < 0 || target < 0 || target >= categories.length) return [];
  const next = [...categories];
  [next[idx], next[target]] = [next[target], next[idx]];
  return next
    .map((c, i) => ({ id: c.id, sortOrder: i, prev: c.sortOrder }))
    .filter((c) => c.sortOrder !== c.prev)
    .map(({ id: cid, sortOrder }) => ({ id: cid, sortOrder }));
}

/** 그룹 접힘 키 — 미분류는 id가 null이라 별도 문자열. */
function groupKeyOf(g: { id: string | null }): string {
  return g.id ?? '__uncategorized__';
}

function formatUpdatedAt(iso: string): { short: string; full: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { short: '', full: '' };
  return {
    short: d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }).replace(/\.\s*$/, ''),
    full: d.toLocaleString('ko-KR'),
  };
}

export interface TemplateExplorerProps {
  templates: readonly StoredReportTemplate[];
  categories: readonly ReportTemplateCategory[];
  listLoading: boolean;
  /** 현재 열린(저장된) 양식 key — 행 하이라이트. 신규 미저장은 null. */
  currentKey: string | null;
  /** 열린 양식에 미저장 변경 — 현재 행에 점 표시. */
  isDirty: boolean;
  /** 호스트가 바쁜 동안(저장·로드 중) 열기·배정을 막는다. */
  busy: boolean;
  /**
   * 검색 입력 포커스 요청 — 값이 바뀌면(그리고 마운트 시) 검색 입력에 포커스한다. Ctrl+K·접힌
   * 레일 아이콘 클릭이 펼침과 함께 올린다(펼침으로 마운트되는 경우엔 마운트 effect가 받는다).
   */
  focusSearchTick: number;
  onCollapse: () => void;
  /** 열기 — 반드시 EditorPage.handleLoad(미저장 확인 모달 경로). */
  onOpen: (key: string) => void;
  /** 새 양식 시작 — 분류 헤더 ⋯에서 부르면 그 분류가 미리 붙는다(null=미분류). */
  onNewTemplate: (categoryId: string | null) => void;
  onImport: () => void;
  onManageCategories: () => void;
  onDuplicate: (key: string) => void;
  onTogglePublish: (key: string, next: boolean) => void;
  onDeleteTemplate: (key: string) => void;
  /** 배정이 서버에 반영된 뒤(성공분만) — 호스트가 목록을 다시 읽고 열린 양식의 속성 select를 맞춘다. */
  onAssigned: (moved: { key: string; categoryId: string | null }[]) => void;
  /** 분류 이름/순서/삭제가 반영된 뒤 — 호스트가 분류·목록을 다시 읽는다. */
  onCategoriesChanged: () => void;
}

type MenuState =
  | { kind: 'new'; x: number; y: number }
  | { kind: 'bulk'; x: number; y: number }
  | { kind: 'category'; x: number; y: number; group: ExplorerTreeGroup }
  | { kind: 'template'; x: number; y: number; template: StoredReportTemplate };

type ConfirmState =
  | { kind: 'delete-category'; category: ReportTemplateCategory }
  | { kind: 'bulk-move'; keys: string[]; categoryId: string | null; label: string };

const VISIBILITY_OPTIONS: { value: ExplorerVisibility; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'enabled', label: '공개' },
  { value: 'disabled', label: '비공개' },
];

/**
 * 탐색기 아이콘(카드 인덱스·분류 묶음) — 인라인 SVG. 이모지(🗂)는 OS 컬러 폰트로 렌더돼
 * 헤더의 다른 기호(≡·⋯·▸)와 결이 달라 currentColor 선화로 둔다. 장식이라 aria-hidden.
 */
function ExplorerIcon({ className = 'h-4 w-4' }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M3 11h18" />
    </svg>
  );
}

const SMALL_BTN =
  'inline-flex h-6 items-center justify-center rounded border border-gray-300 bg-white px-1.5 text-xs ' +
  'text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

/** 버튼 아래에 메뉴를 띄울 좌표(버튼 rect 좌하단). */
function menuAnchor(el: HTMLElement): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.bottom + 2 };
}

export function TemplateExplorer({
  templates,
  categories,
  listLoading,
  currentKey,
  isDirty,
  busy,
  focusSearchTick,
  onCollapse,
  onOpen,
  onNewTemplate,
  onImport,
  onManageCategories,
  onDuplicate,
  onTogglePublish,
  onDeleteTemplate,
  onAssigned,
  onCategoriesChanged,
}: TemplateExplorerProps): JSX.Element {
  const showToast = useToastStore((s) => s.showToast);
  const [query, setQuery] = useState('');
  const [visibility, setVisibility] = useState<ExplorerVisibility>('all');
  // 사용자가 접은 그룹(필터에 의한 접힘은 트리 함수의 collapsedByFilter가 따로 말한다).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const anchorKeyRef = useRef<string | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const draggingKeysRef = useRef<string[]>([]);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const renameRef = useRef<HTMLInputElement>(null);

  const tree = useMemo(
    () => buildExplorerTree(templates, categories, { query, visibility }),
    [templates, categories, query, visibility],
  );
  const filtering = query.trim() !== '' || visibility !== 'all';
  const templateByKey = useMemo(() => {
    const m = new Map<string, StoredReportTemplate>();
    for (const t of templates) m.set(t.key, t);
    return m;
  }, [templates]);
  // 화면에 보이는 행의 평면 순서 — Shift 범위 선택의 축.
  const visibleKeys = useMemo(() => {
    const out: string[] = [];
    for (const g of tree) {
      if (g.collapsedByFilter || collapsedGroups.has(groupKeyOf(g))) continue;
      for (const t of g.templates) out.push(t.key);
    }
    return out;
  }, [tree, collapsedGroups]);

  // 검색 포커스 요청(Ctrl+K·레일 아이콘) — 마운트 시에도 한 번(펼침으로 마운트되는 경로).
  useEffect(() => {
    searchRef.current?.focus();
    searchRef.current?.select();
  }, [focusSearchTick]);

  useEffect(() => {
    if (renaming) renameRef.current?.focus();
  }, [renaming]);

  // 목록에서 사라진 선택은 정리(삭제·필터와 무관하게 존재 기준).
  useEffect(() => {
    setSelectedKeys((prev) => {
      const next = new Set([...prev].filter((k) => templateByKey.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [templateByKey]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── 배정(assign) — 순차 PATCH { categoryId }만(본문 무접촉). 이미 그 분류인 양식은 건너뛴다. ──
  const assignTemplates = useCallback(
    async (keys: string[], categoryId: string | null, label: string) => {
      const targets = keys
        .map((k) => templateByKey.get(k))
        .filter((t): t is StoredReportTemplate => !!t && (t.categoryId ?? null) !== categoryId);
      if (targets.length === 0) {
        showToast('이미 그 분류에 있는 양식입니다.', 'info');
        return;
      }
      setAssignBusy(true);
      const moved: { key: string; categoryId: string | null }[] = [];
      const failed: string[] = [];
      let lastError: string | undefined;
      try {
        for (const t of targets) {
          try {
            const res = await adminApi.updateReportTemplate(t.key, { categoryId });
            if (res.success) moved.push({ key: t.key, categoryId });
            else {
              failed.push(t.name);
              lastError = res.error?.message;
            }
          } catch (err) {
            failed.push(t.name);
            lastError = extractApiError(err).message;
          }
        }
      } finally {
        setAssignBusy(false);
      }
      if (moved.length > 0) {
        showToast(
          moved.length === 1
            ? `${withObjectParticle(`「${templateByKey.get(moved[0].key)?.name ?? moved[0].key}」`)} ${withDirectionParticle(label)} 이동했습니다.`
            : `양식 ${moved.length}개를 ${withDirectionParticle(label)} 이동했습니다.`,
          'success',
        );
        onAssigned(moved);
      }
      if (failed.length > 0) {
        showToast(
          `이동 실패: ${failed.join(', ')}${lastError ? ` — ${lastError}` : ''}`,
          'error',
        );
      }
    },
    [templateByKey, showToast, onAssigned],
  );

  // ── 드래그(HTML5 DnD) — 선택에 포함된 행을 끌면 선택 전체, 아니면 그 행만. ──
  const onRowDragStart = useCallback(
    (e: React.DragEvent<HTMLLIElement>, key: string) => {
      const keys = selectedKeys.has(key) ? [...selectedKeys] : [key];
      draggingKeysRef.current = keys;
      try {
        e.dataTransfer.setData('text/plain', keys.join(','));
        e.dataTransfer.effectAllowed = 'move';
      } catch {
        // jsdom 등 dataTransfer 미구현 — ref가 정본이라 무해.
      }
    },
    [selectedKeys],
  );
  const onRowDragEnd = useCallback(() => {
    draggingKeysRef.current = [];
    setDropTarget(null);
  }, []);
  const onGroupDragOver = useCallback(
    (e: React.DragEvent<HTMLLIElement>, g: ExplorerTreeGroup) => {
      if (g.orphan || assignBusy) return; // 목록에 없는 분류는 드롭 대상이 아니다.
      e.preventDefault();
      try {
        e.dataTransfer.dropEffect = 'move';
      } catch {
        // dataTransfer 미구현 무시
      }
      const gk = groupKeyOf(g);
      setDropTarget((cur) => (cur === gk ? cur : gk));
    },
    [assignBusy],
  );
  const onGroupDragLeave = useCallback((e: React.DragEvent<HTMLLIElement>) => {
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    setDropTarget(null);
  }, []);
  const onGroupDrop = useCallback(
    (e: React.DragEvent<HTMLLIElement>, g: ExplorerTreeGroup) => {
      if (g.orphan || assignBusy) return;
      e.preventDefault();
      setDropTarget(null);
      let keys = draggingKeysRef.current;
      if (keys.length === 0) {
        let text = '';
        try {
          text = e.dataTransfer.getData('text/plain');
        } catch {
          text = '';
        }
        keys = text ? text.split(',').filter(Boolean) : [];
      }
      draggingKeysRef.current = [];
      if (keys.length === 0) return;
      void assignTemplates(keys, g.id, `「${g.name}」`);
    },
    [assignBusy, assignTemplates],
  );

  // ── 행 클릭 — 일반=열기 / Ctrl=토글 선택 / Shift=범위 선택. ──
  const onRowClick = useCallback(
    (e: React.MouseEvent, key: string) => {
      if (e.ctrlKey || e.metaKey) {
        setSelectedKeys((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
        anchorKeyRef.current = key;
        return;
      }
      if (e.shiftKey) {
        const anchor = anchorKeyRef.current ?? key;
        const a = visibleKeys.indexOf(anchor);
        const b = visibleKeys.indexOf(key);
        if (a < 0 || b < 0) {
          setSelectedKeys(new Set([key]));
        } else {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          setSelectedKeys(new Set(visibleKeys.slice(lo, hi + 1)));
        }
        return;
      }
      anchorKeyRef.current = key;
      if (busy) return;
      onOpen(key);
    },
    [visibleKeys, busy, onOpen],
  );

  // ── 분류 헤더 액션 ──
  // 이름 변경 커밋은 Enter와 blur 두 경로에서 오므로(Enter로 입력이 언마운트되며 blur가 뒤따를 수 있다)
  // ref로 1회만 소비한다 — 같은 값을 두 번 PATCH하지 않게.
  const renamingRef = useRef(renaming);
  renamingRef.current = renaming;
  const commitRename = useCallback(async () => {
    const pending = renamingRef.current;
    if (!pending) return;
    renamingRef.current = null;
    const name = pending.name.trim();
    const cur = categories.find((c) => c.id === pending.id);
    setRenaming(null);
    if (!cur || !name || name === cur.name) return;
    try {
      const res = await adminApi.updateReportTemplateCategory(cur.id, { name });
      if (res.success) {
        showToast(`분류 이름을 ${withDirectionParticle(`「${name}」`)} 바꿨습니다.`, 'success');
        onCategoriesChanged();
      } else showToast(res.error?.message ?? '이름 변경에 실패했습니다.', 'error');
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    }
  }, [categories, showToast, onCategoriesChanged]);

  const moveCategory = useCallback(
    async (id: string, dir: -1 | 1) => {
      const plan = planCategoryMove(categories, id, dir);
      if (plan.length === 0) return;
      let failedAny = false;
      for (const u of plan) {
        try {
          const res = await adminApi.updateReportTemplateCategory(u.id, { sortOrder: u.sortOrder });
          if (!res.success) failedAny = true;
        } catch {
          failedAny = true;
        }
      }
      if (failedAny) showToast('분류 순서 변경 중 일부가 실패했습니다.', 'error');
      onCategoriesChanged();
    },
    [categories, showToast, onCategoriesChanged],
  );

  const deleteCategory = useCallback(
    async (category: ReportTemplateCategory) => {
      try {
        const res = await adminApi.deleteReportTemplateCategory(category.id);
        if (res.success) {
          showToast(
            `분류 ${withObjectParticle(`「${category.name}」`)} 삭제했습니다${
              res.data && res.data.movedToUncategorized > 0
                ? ` — 양식 ${res.data.movedToUncategorized}개가 ${UNCATEGORIZED_LABEL}로 이동`
                : ''
            }.`,
            'success',
          );
          onCategoriesChanged();
        } else showToast(res.error?.message ?? '분류 삭제에 실패했습니다.', 'error');
      } catch (err) {
        showToast(extractApiError(err).message, 'error');
      } finally {
        setConfirm(null);
      }
    },
    [showToast, onCategoriesChanged],
  );

  // ── 메뉴 항목 조립 ──
  const closeMenu = useCallback(() => setMenu(null), []);
  const menuItems = useMemo((): ComposerContextMenuItem[] => {
    if (!menu) return [];
    if (menu.kind === 'new') {
      return [
        { type: 'action', label: '새 양식', onSelect: () => onNewTemplate(null) },
        {
          type: 'action',
          // ⚠️ 지원 형식을 늘리면 **이 라벨도** 고쳐라 — 운영자가 가장 먼저 보는 진입 문구라
          // 여기 없는 형식은 "안 된다"로 읽힌다. 2026-10-04 HWP 지원(P8) 때 모달 2곳·매뉴얼
          // 3곳은 고쳐졌는데 이 한 줄이 남아 dev 실측에서야 드러났다(spec은 `/양식 가져오기/`
          // 부분 매칭이라 안 깨진다). 형식 열거는 `FormImportModal`의 안내와 함께 센다.
          //
          // ⭐ 이제 **탐지기가 있다** — `formImportSupportedFormats.guard.test.ts`가
          // `FormImportModal.ACCEPT_MIME`에서 토큰을 뽑아 이 라벨과 모달 안내 양쪽에 그 형식의
          // 낱말이 있는지 센다(2026-10-05 신설). 라벨 문장을 바꿔도 되지만 **형식 낱말은
          // 남겨야** 하고, accept에 새 형식을 더하면 그 가드가 「매핑 없는 형식」으로 먼저 막는다.
          label: '양식 가져오기(이미지·PDF·엑셀·한글)',
          onSelect: onImport,
        },
      ];
    }
    if (menu.kind === 'bulk') {
      const keys = [...selectedKeys];
      const target = (categoryId: string | null, label: string): ComposerContextMenuItem => ({
        type: 'action',
        label,
        onSelect: () => setConfirm({ kind: 'bulk-move', keys, categoryId, label: `「${label}」` }),
      });
      return [
        ...categories.map((c) => target(c.id, c.name)),
        ...(categories.length > 0 ? [{ type: 'separator' } as const] : []),
        target(null, UNCATEGORIZED_LABEL),
      ];
    }
    if (menu.kind === 'category') {
      const g = menu.group;
      if (g.id === null || g.orphan) {
        return [
          { type: 'action', label: '이 분류에 새 양식', onSelect: () => onNewTemplate(null) },
        ];
      }
      const idx = categories.findIndex((c) => c.id === g.id);
      const cat = categories[idx];
      return [
        {
          type: 'action',
          label: '이름 변경',
          onSelect: () => setRenaming({ id: g.id as string, name: g.name }),
        },
        {
          type: 'action',
          label: '위로',
          disabled: idx <= 0,
          onSelect: () => void moveCategory(g.id as string, -1),
        },
        {
          type: 'action',
          label: '아래로',
          disabled: idx < 0 || idx >= categories.length - 1,
          onSelect: () => void moveCategory(g.id as string, 1),
        },
        { type: 'separator' },
        { type: 'action', label: '이 분류에 새 양식', onSelect: () => onNewTemplate(g.id) },
        { type: 'separator' },
        {
          type: 'action',
          label: '삭제',
          danger: true,
          disabled: !cat,
          onSelect: () => cat && setConfirm({ kind: 'delete-category', category: cat }),
        },
      ];
    }
    const t = menu.template;
    return [
      {
        type: 'action',
        label: '열기',
        disabled: busy,
        onSelect: () => onOpen(t.key),
      },
      {
        type: 'action',
        label: '복제',
        disabled: busy,
        onSelect: () => onDuplicate(t.key),
      },
      {
        type: 'action',
        label: t.enabled ? '비공개로 전환' : '공개',
        disabled: busy,
        onSelect: () => onTogglePublish(t.key, !t.enabled),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: '삭제',
        danger: true,
        disabled: busy,
        onSelect: () => onDeleteTemplate(t.key),
      },
    ];
  }, [
    menu,
    selectedKeys,
    categories,
    busy,
    onNewTemplate,
    onImport,
    onOpen,
    onDuplicate,
    onTogglePublish,
    onDeleteTemplate,
    moveCategory,
  ]);

  // Esc — 탐색기 안에 포커스가 있을 때 접는다(메뉴·모달·이름 변경 입력은 각자 Esc를 먹는다).
  const onRootKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Escape' || menu || confirm) return;
      e.preventDefault();
      e.stopPropagation();
      onCollapse();
    },
    [menu, confirm, onCollapse],
  );

  const selectedCount = selectedKeys.size;

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-white text-sm"
      data-testid="template-explorer"
      onKeyDown={onRootKeyDown}
    >
      {/* 헤더 — 제목 · ＋ 새 양식 ▾ · 접기 */}
      <div className="flex items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1">
        <span className="flex flex-1 items-center gap-1 truncate text-xs font-medium text-gray-700">
          <ExplorerIcon className="h-3.5 w-3.5 shrink-0" />
          양식 탐색기
        </span>
        <button
          type="button"
          className={SMALL_BTN}
          disabled={busy}
          title="새 양식 시작 · 양식 가져오기"
          aria-haspopup="menu"
          onClick={(e) => setMenu({ kind: 'new', ...menuAnchor(e.currentTarget) })}
        >
          ＋ 새 양식 ▾
        </button>
        <button
          type="button"
          className={SMALL_BTN}
          onClick={onCollapse}
          title="탐색기 접기 (Esc)"
          aria-label="탐색기 접기"
        >
          «
        </button>
      </div>

      {/* 검색 + 공개 필터 */}
      <div className="flex flex-col gap-1 border-b border-gray-200 px-2 py-1.5">
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="이름·키 검색 (Ctrl+K)"
          aria-label="양식 검색"
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
        />
        <div
          className="inline-flex rounded border border-gray-300 bg-white p-0.5"
          role="group"
          aria-label="공개 필터"
        >
          {VISIBILITY_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              aria-pressed={visibility === o.value}
              onClick={() => setVisibility(o.value)}
              className={`flex-1 rounded px-1.5 py-0.5 text-[11px] ${
                visibility === o.value ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* 분류 트리 */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {listLoading && templates.length === 0 ? (
          <p className="px-3 py-2 text-xs text-gray-400">목록 불러오는 중…</p>
        ) : templates.length === 0 ? (
          <p className="px-3 py-2 text-xs text-gray-400">저장된 양식이 없습니다.</p>
        ) : null}
        <ul role="tree" aria-label="양식 분류 트리" className="py-1">
          {tree.map((g) => {
            const gk = groupKeyOf(g);
            const collapsed = g.collapsedByFilter || collapsedGroups.has(gk);
            const isDrop = dropTarget === gk;
            const isUncategorized = g.id === null;
            return (
              <li
                key={gk}
                role="treeitem"
                aria-expanded={!collapsed}
                aria-label={g.name}
                data-testid={`explorer-group:${gk}`}
                data-drop-target={isDrop ? 'true' : undefined}
                className={isDrop ? 'bg-brand-50 ring-1 ring-inset ring-brand-300' : undefined}
                onDragOver={(e) => onGroupDragOver(e, g)}
                onDragLeave={onGroupDragLeave}
                onDrop={(e) => onGroupDrop(e, g)}
              >
                <div className="group flex items-center gap-1 px-1.5 py-0.5">
                  {renaming && renaming.id === g.id ? (
                    <input
                      ref={renameRef}
                      value={renaming.name}
                      aria-label="분류 이름"
                      onChange={(e) => setRenaming({ id: renaming.id, name: e.target.value })}
                      onBlur={() => void commitRename()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void commitRename();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          renamingRef.current = null; // 뒤따르는 blur가 취소한 값을 커밋하지 않게.
                          setRenaming(null);
                        }
                      }}
                      className="min-w-0 flex-1 rounded border border-brand-400 px-1 py-0.5 text-xs"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleGroup(gk)}
                      className="flex min-w-0 flex-1 items-center gap-1 text-left text-xs font-medium text-gray-700"
                      title={collapsed ? '펼치기' : '접기'}
                    >
                      <span className="w-3 text-gray-400" aria-hidden>
                        {collapsed ? '▸' : '▾'}
                      </span>
                      <span className={`truncate ${g.orphan ? 'italic text-gray-500' : ''}`}>
                        {g.name}
                      </span>
                      <span
                        className={`ml-auto shrink-0 rounded px-1 text-[10px] ${
                          isUncategorized && g.total > 0
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                        title={
                          isUncategorized && g.total > 0
                            ? `분류가 없는 양식 ${g.total}개`
                            : `양식 ${g.total}개`
                        }
                      >
                        {isUncategorized && g.total > 0 ? '⚠ ' : ''}
                        {filtering ? `${g.templates.length}/${g.total}` : g.total}
                      </span>
                    </button>
                  )}
                  {!g.orphan && (
                    <button
                      type="button"
                      className="h-5 w-5 shrink-0 rounded text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      title="분류 메뉴"
                      aria-label={`${g.name} 분류 메뉴`}
                      aria-haspopup="menu"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenu({ kind: 'category', group: g, ...menuAnchor(e.currentTarget) });
                      }}
                    >
                      ⋯
                    </button>
                  )}
                </div>
                {!collapsed && (
                  <ul role="group">
                    {g.templates.length === 0 && (
                      <li className="px-6 py-0.5 text-[11px] text-gray-300">
                        {filtering ? '검색 결과 없음' : '(비어 있음 — 양식을 끌어다 놓으세요)'}
                      </li>
                    )}
                    {g.templates.map((t) => {
                      const isCurrent = currentKey === t.key;
                      const isSelected = selectedKeys.has(t.key);
                      const updated = formatUpdatedAt(t.updatedAt);
                      return (
                        <li
                          key={t.key}
                          role="treeitem"
                          aria-selected={isSelected}
                          aria-current={isCurrent ? 'true' : undefined}
                          aria-label={t.name}
                          tabIndex={0}
                          draggable={!assignBusy}
                          data-testid={`explorer-row:${t.key}`}
                          onDragStart={(e) => onRowDragStart(e, t.key)}
                          onDragEnd={onRowDragEnd}
                          onClick={(e) => onRowClick(e, t.key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (!busy) onOpen(t.key);
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setMenu({ kind: 'template', template: t, x: e.clientX, y: e.clientY });
                          }}
                          className={`group flex cursor-pointer items-center gap-1 py-0.5 pl-5 pr-1 outline-none focus-visible:ring-1 focus-visible:ring-brand-400 ${
                            isSelected
                              ? 'bg-brand-100'
                              : isCurrent
                                ? 'bg-brand-50'
                                : 'hover:bg-gray-50'
                          }`}
                          title={`${t.name} · ${t.key}${updated.full ? ` · 수정 ${updated.full}` : ''}`}
                        >
                          <span
                            className={`w-3 shrink-0 text-center text-[10px] ${
                              t.enabled ? 'text-green-600' : 'text-gray-400'
                            }`}
                            aria-label={t.enabled ? '공개' : '비공개'}
                            title={t.enabled ? '공개' : '비공개'}
                          >
                            {t.enabled ? '●' : '○'}
                          </span>
                          <span
                            className={`min-w-0 flex-1 truncate text-xs ${
                              isCurrent ? 'font-semibold text-brand-700' : 'text-gray-800'
                            }`}
                          >
                            {t.name}
                            {isCurrent && isDirty && (
                              <span className="ml-1 text-amber-600" title="저장하지 않은 변경">
                                •
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 text-[10px] text-gray-400" title={updated.full}>
                            {updated.short}
                          </span>
                          <button
                            type="button"
                            className="h-5 w-5 shrink-0 rounded text-xs text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-700 focus:opacity-100 group-hover:opacity-100"
                            title="양식 메뉴"
                            aria-label={`${t.name} 양식 메뉴`}
                            aria-haspopup="menu"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenu({ kind: 'template', template: t, ...menuAnchor(e.currentTarget) });
                            }}
                          >
                            ⋯
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* 하단 — 선택 일괄 이동 · 분류 관리 */}
      <div className="flex flex-col gap-1 border-t border-gray-200 bg-gray-50 px-2 py-1.5">
        {selectedCount > 0 && (
          <div className="flex items-center gap-1 text-[11px] text-gray-600">
            <span className="flex-1">{selectedCount}개 선택</span>
            <button
              type="button"
              className={SMALL_BTN}
              disabled={assignBusy}
              aria-haspopup="menu"
              title="선택한 양식을 다른 분류로 이동"
              onClick={(e) => setMenu({ kind: 'bulk', ...menuAnchor(e.currentTarget) })}
            >
              분류 이동 ▾
            </button>
            <button
              type="button"
              className={SMALL_BTN}
              onClick={() => setSelectedKeys(new Set())}
              title="선택 해제"
            >
              해제
            </button>
          </div>
        )}
        <button
          type="button"
          className={`${SMALL_BTN} w-full inline-flex items-center justify-center gap-1`}
          onClick={onManageCategories}
          disabled={busy}
          title="양식 분류 추가·이름 변경·정렬·삭제"
        >
          <ExplorerIcon className="h-3.5 w-3.5" />
          분류 관리
        </button>
        {assignBusy && <span className="text-[10px] text-gray-400">이동 중…</span>}
      </div>

      {menu ? (
        <ComposerContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={closeMenu} />
      ) : null}

      {/* 분류 삭제 확인(danger) — 소속 양식은 서버가 미분류로 돌린다. */}
      <ConfirmModal
        open={confirm?.kind === 'delete-category'}
        title="분류 삭제"
        body={
          confirm?.kind === 'delete-category'
            ? `분류 ${withObjectParticle(`「${confirm.category.name}」`)} 삭제하시겠습니까? 양식 ${confirm.category.templateCount}개가 ${UNCATEGORIZED_LABEL}로 이동합니다.`
            : ''
        }
        confirmLabel="삭제"
        danger
        onConfirm={() => confirm?.kind === 'delete-category' && void deleteCategory(confirm.category)}
        onClose={() => setConfirm(null)}
      />
      {/* 일괄 이동 확인(danger) — 여러 양식의 분류가 한 번에 바뀐다. */}
      <ConfirmModal
        open={confirm?.kind === 'bulk-move'}
        title="분류 이동"
        body={
          confirm?.kind === 'bulk-move'
            ? `선택한 양식 ${confirm.keys.length}개를 ${withDirectionParticle(confirm.label)} 이동하시겠습니까?`
            : ''
        }
        confirmLabel="이동"
        danger
        busy={assignBusy}
        onConfirm={() => {
          if (confirm?.kind !== 'bulk-move') return;
          const { keys, categoryId, label } = confirm;
          setConfirm(null);
          setSelectedKeys(new Set());
          void assignTemplates(keys, categoryId, label);
        }}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}

/** 접힌 레일(48px) — 펼치기 아이콘 + 새 양식. 셸 renderCompact가 그린다. */
export function TemplateExplorerRail({
  onExpand,
  onNewTemplate,
  busy,
}: {
  onExpand: () => void;
  onNewTemplate: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center gap-1 bg-gray-50 py-2">
      <button
        type="button"
        onClick={onExpand}
        title="양식 탐색기 펼치기 (Ctrl+K)"
        aria-label="양식 탐색기 펼치기"
        className="flex h-8 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-200"
      >
        <ExplorerIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={onNewTemplate}
        disabled={busy}
        title="새 양식 시작"
        aria-label="새 양식 시작"
        className="flex h-8 w-8 items-center justify-center rounded text-base text-gray-600 hover:bg-gray-200 disabled:opacity-50"
      >
        ＋
      </button>
    </div>
  );
}
